import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, Field, Icon, Modal } from '@evogard/ui';
import type { Period } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { periodLabel } from './common.js';

/**
 * Recalcular o extrato de UM consultor — inclusive na competência fechada
 * (26/10/2026).
 *
 * O recálculo e a reabertura do modal de Pagamentos são da praça inteira.
 * Este fica no Extrato da pessoa, para quem tem `sales.statement:fechar`, e
 * reescreve só as linhas dela: o mês continua fechado para os outros. No mês
 * fechado pede o motivo, e o servidor recusa se ela já enviou a nota — ver
 * `recalculateConsultant`.
 */
export function StatementConsultantRecalculate({
  month,
  consultant,
  name,
  closed,
}: {
  month: Period;
  /** O código do voluntário na base. */
  consultant: string;
  name: string;
  closed: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const recalculate = useMutation({
    mutationFn: async () =>
      ok(
        await api.sales.recalculateConsultantStatement({
          body: {
            month,
            consultant,
            ...(closed ? { reason: reason.trim() } : {}),
          },
        }),
        200,
        'Não foi possível recalcular o extrato.',
      ),
    /*
     * Invalida TUDO, como os atos do modal de Pagamentos: o recálculo muda o
     * Extrato, o saldo do topo e os lotes da gestão.
     */
    onSuccess: () => void queryClient.invalidateQueries(),
  });

  const dismiss = () => {
    setOpen(false);
    setReason('');
    recalculate.reset();
  };

  return (
    <>
      <Button variant="contorno" size="sm" onClick={() => setOpen(true)}>
        <Icon nome="atualizar" /> Recalcular este extrato
      </Button>

      <Modal
        open={open}
        onOpenChange={(next) => !next && dismiss()}
        title="Recalcular este extrato"
        descricao={
          closed
            ? `Reescreve as entradas de ${name} em ${periodLabel(month)} com o cadastro e o espelho de agora. A competência está fechada e continua fechada para os outros consultores. Não é possível se ${name} já enviou a nota fiscal deste mês.`
            : `Reescreve as entradas de ${name} em ${periodLabel(month)} com o cadastro e o espelho de agora. Os outros consultores não mudam.`
        }
        size="md"
      >
        <div className="flex flex-col gap-4">
          {closed && (
            <Field
              label="Por que está recalculando?"
              name="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              help="Fica gravado junto com o seu nome e a hora."
            />
          )}

          <div className="flex justify-end">
            <Button
              disabled={
                recalculate.isPending ||
                recalculate.isSuccess ||
                (closed && reason.trim().length < 5)
              }
              onClick={() => recalculate.mutate()}
            >
              Recalcular {periodLabel(month)}
            </Button>
          </div>

          {recalculate.error && <Alert tone="erro">{recalculate.error.message}</Alert>}

          {/*
           * As duas coisas, como no recálculo da praça: quantas entradas
           * regravou e quais premiações o espelho não respondeu.
           */}
          {recalculate.isSuccess && (
            <Alert tone={recalculate.data.refused.length > 0 ? 'alerta' : 'sucesso'}>
              {recalculate.data.entries === 1
                ? '1 entrada regravada com o cadastro de agora.'
                : `${recalculate.data.entries} entradas regravadas com o cadastro de agora.`}
              {recalculate.data.refused.length > 0 && (
                <>
                  {' '}
                  {recalculate.data.refused.length === 1
                    ? 'Uma premiação não pôde ser apurada'
                    : `${recalculate.data.refused.length} premiações não puderam ser apuradas`}{' '}
                  porque o espelho do ERP não respondeu, e as entradas delas ficaram como
                  estavam: {recalculate.data.refused.map((r) => r.award).join(', ')}.
                </>
              )}
            </Alert>
          )}
        </div>
      </Modal>
    </>
  );
}
